import { useRef } from "react";
import { Send, Percent, Waves, MessageSquareText, ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    id: "capital-call-engine",
    index: "01",
    icon: Send,
    title: "Capital Call Engine",
    line: "1,200 notices. 30 minutes.",
    body: "Allocations computed against commitments, LPA terms, and prior calls. Notices drafted, validated, reviewed, and delivered to every LP in a single run. One client watched a 1,200-investor call complete in half an hour. Their team had been budgeting two to three days for it.",
  },
  {
    id: "management-fee-ai",
    index: "02",
    icon: Percent,
    title: "Management Fee AI",
    line: "Fees that calculate themselves.",
    body: "Step-downs, tiered rates, mid-period closings, rebates, and recycled capital handled at the term level. Change a commitment and every affected period recalculates instantly, with the math shown and traceable.",
  },
  {
    id: "waterfall-ai",
    index: "03",
    icon: Waves,
    title: "Waterfall AI",
    line: "Model the distribution before the money moves.",
    body: "European and American structures, preferred return, catch-up, tiers, and clawback. Run the scenario, see carry and LP proceeds at the investor level, and distribute only once the numbers hold.",
  },
  {
    id: "lp-intelligence",
    index: "04",
    icon: MessageSquareText,
    title: "LP Intelligence",
    line: "Your investors stop emailing you.",
    body: "LPs log in and ask questions in plain language. Capital account, called and uncalled capital, IRR, distributions, and documents answered in real time from the same ledger your accountants work on. One source of truth for the GP and the LP at the same time.",
  },
];

const TILT_MAX = 7;

const ServiceCard = ({ service }) => {
  const ref = useRef(null);
  const frame = useRef(0);
  const Icon = service.icon;

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      const rx = (0.5 - py) * TILT_MAX;
      const ry = (px - 0.5) * TILT_MAX;
      el.style.transform = `perspective(1100px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-4px)`;
      el.style.setProperty("--mx", `${px * 100}%`);
      el.style.setProperty("--my", `${py * 100}%`);
    });
  };

  const onLeave = () => {
    const el = ref.current;
    if (!el) return;
    cancelAnimationFrame(frame.current);
    el.style.transform = "perspective(1100px) rotateX(0deg) rotateY(0deg) translateY(0px)";
  };

  return (
    <article
      ref={ref}
      data-testid={`service-card-${service.id}`}
      tabIndex={0}
      onPointerMove={onMove}
      onPointerLeave={onLeave}
      onBlur={onLeave}
      className="axt-glass axt-tilt group relative p-7 sm:p-8 lg:p-9 flex flex-col outline-none focus-visible:ring-1 focus-visible:ring-white/70"
      style={{ transition: "transform 600ms cubic-bezier(0.16,1,0.3,1), box-shadow 300ms ease" }}
    >
      <div className="axt-tilt-glow absolute inset-0 rounded-[inherit] pointer-events-none" />
      {/* hairline that sweeps in on hover */}
      <span
        className="absolute left-7 right-7 top-0 h-px origin-left scale-x-0 group-hover:scale-x-100 group-focus-visible:scale-x-100 bg-[color:var(--axt-accent)]"
        style={{ transition: "transform 600ms cubic-bezier(0.16,1,0.3,1)", opacity: 0.8 }}
        aria-hidden="true"
      />
      <div className="relative flex items-start justify-between">
        <div className="axt-metal w-11 h-11 rounded-sm flex items-center justify-center text-[color:var(--axt-text-dim)] group-hover:text-[color:var(--axt-accent)] transition-colors duration-300">
          <Icon className="w-5 h-5 transition-transform duration-500 group-hover:-translate-y-0.5" strokeWidth={1.6} />
        </div>
        <span className="font-mono-axt text-xs tracking-[0.18em] text-[color:var(--axt-text-faint)]">{service.index}</span>
      </div>
      <p className="relative mt-7 font-mono-axt text-[11px] tracking-[0.18em] uppercase text-[color:var(--axt-text-faint)]">{service.title}</p>
      <h3 className="relative mt-3 font-display text-2xl sm:text-3xl leading-[1.1] text-[color:var(--axt-text)]">{service.line}</h3>
      <p className="relative mt-4 text-sm sm:text-[15px] leading-relaxed text-[color:var(--axt-text-dim)]">{service.body}</p>
      <div className="relative mt-auto pt-7 flex items-center justify-end">
        <ArrowUpRight
          className="w-4 h-4 text-[color:var(--axt-text-faint)] opacity-0 -translate-x-1 translate-y-1 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-[opacity,transform] duration-300"
          aria-hidden="true"
        />
      </div>
    </article>
  );
};

export const ServicesGrid = () => (
  <section id="services" data-testid="services-section" className="relative py-24 sm:py-32 lg:py-40">
    <div className="absolute inset-x-0 top-0 axt-hairline" />
    <div className="max-w-6xl mx-auto px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-end" data-reveal>
        <div className="lg:col-span-7">
          <p className="axt-rule mb-5">
            <span>003 · The product stack</span>
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-[-0.02em] text-[color:var(--axt-text)]">
            The engine behind the close
          </h2>
        </div>
        <p className="lg:col-span-5 text-base leading-relaxed text-[color:var(--axt-text-dim)]">Four systems, one platform, built for funds that refuse to wait.</p>
      </div>

      <div className="mt-14 sm:mt-16 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5" data-reveal>
        {SERVICES.map((s) => (
          <ServiceCard key={s.id} service={s} />
        ))}
      </div>

      <p className="mt-8 axt-rule" data-testid="services-footer-line">
        <span className="normal-case tracking-[0.06em] text-[color:var(--axt-text-dim)]">More agents are in build. Every one of them ships into the same ledger.</span>
      </p>
    </div>
  </section>
);
